import { type FormEvent, useId, useState } from 'react';

import { type BriefDetails, FUNNEL_STAGES, type FunnelStage } from './api.ts';
import { describeError } from './data.tsx';
import { Icon } from './Icon.tsx';
import { Facts, Notice, Panel } from './ui.tsx';

/**
 * A content's brief details — goal, audience, funnel, angle, placement, placed
 * and free extra pairs — read and edited. The fields are shared by the content
 * page, the New content form (US-004) and idea review (US-007), so the plan
 * for a piece is written the same way wherever it is written.
 */

export const FUNNEL_LABEL: Record<FunnelStage, string> = {
  cold: 'Cold',
  warm: 'Warm',
  hot: 'Hot',
  retention: 'Retention',
};

/** One extra-detail row while it is edited; `id` keeps React's rows apart. */
export interface ExtraDetailRow {
  readonly id: number;
  readonly key: string;
  readonly value: string;
}

/** The details as a form holds them: every text a string, the extras with row ids. */
export interface BriefDetailsDraft {
  readonly goal: string;
  readonly audience: string;
  readonly angle: string;
  readonly placement: string;
  readonly funnelStages: readonly FunnelStage[];
  readonly placed: boolean;
  readonly extraDetails: readonly ExtraDetailRow[];
}

let nextRowId = 1;

function row(key = '', value = ''): ExtraDetailRow {
  return { id: nextRowId++, key, value };
}

export function draftOf(details: BriefDetails): BriefDetailsDraft {
  return {
    goal: details.goal ?? '',
    audience: details.audience ?? '',
    angle: details.angle ?? '',
    placement: details.placement ?? '',
    funnelStages: details.funnelStages,
    placed: details.placed,
    extraDetails: details.extraDetails.map((detail) => row(detail.key, detail.value)),
  };
}

/**
 * What the draft sends. A row left wholly blank is dropped; a half-filled one
 * is sent as it is, so the server names what is missing.
 */
export function detailsOf(draft: BriefDetailsDraft): BriefDetails {
  const text = (value: string): string | null => (value.trim() === '' ? null : value.trim());
  return {
    goal: text(draft.goal),
    audience: text(draft.audience),
    angle: text(draft.angle),
    placement: text(draft.placement),
    funnelStages: FUNNEL_STAGES.filter((stage) => draft.funnelStages.includes(stage)),
    placed: draft.placed,
    extraDetails: draft.extraDetails
      .filter((detail) => detail.key.trim() !== '' || detail.value.trim() !== '')
      .map((detail) => ({ key: detail.key.trim(), value: detail.value.trim() })),
  };
}

const TEXT_FIELDS = [
  ['goal', 'Goal', 'What the piece is for, e.g. sign-ups for the trial'],
  ['audience', 'Audience', 'Who it is aimed at'],
  ['angle', 'Angle', 'The hook or point of view it takes'],
  ['placement', 'Placement', 'Where it will be published, e.g. Instagram reels'],
] as const;

/** The editable fields, controlled: the owner holds the draft. */
export function BriefDetailsFields({
  value,
  onChange,
  disabled = false,
}: {
  readonly value: BriefDetailsDraft;
  readonly onChange: (value: BriefDetailsDraft) => void;
  readonly disabled?: boolean;
}) {
  const id = useId();

  const setRow = (rowId: number, change: Partial<Pick<ExtraDetailRow, 'key' | 'value'>>): void => {
    onChange({
      ...value,
      extraDetails: value.extraDetails.map((detail) =>
        detail.id === rowId ? { ...detail, ...change } : detail,
      ),
    });
  };

  return (
    <div className="form form--tight">
      <div className="field__row">
        {TEXT_FIELDS.map(([field, label, placeholder]) => (
          <div className="field" key={field}>
            <label className="field__label" htmlFor={`${id}-${field}`}>
              {label}
            </label>
            <input
              id={`${id}-${field}`}
              className="field__input"
              type="text"
              value={value[field]}
              placeholder={placeholder}
              maxLength={500}
              disabled={disabled}
              onChange={(event) => {
                onChange({ ...value, [field]: event.target.value });
              }}
            />
          </div>
        ))}
      </div>

      <div className="field">
        <span className="field__label" id={`${id}-funnel`}>
          Funnel
        </span>
        <div className="field__actions" role="group" aria-labelledby={`${id}-funnel`}>
          {FUNNEL_STAGES.map((stage) => {
            const on = value.funnelStages.includes(stage);
            return (
              <button
                type="button"
                key={stage}
                className={`button button--small${on ? ' button--active' : ''}`}
                aria-pressed={on}
                disabled={disabled}
                onClick={() => {
                  onChange({
                    ...value,
                    funnelStages: on
                      ? value.funnelStages.filter((other) => other !== stage)
                      : FUNNEL_STAGES.filter((other) => other === stage || value.funnelStages.includes(other)),
                  });
                }}
              >
                {on && <Icon name="check" />}
                {FUNNEL_LABEL[stage]}
              </button>
            );
          })}
        </div>
      </div>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={value.placed}
          disabled={disabled}
          onChange={(event) => {
            onChange({ ...value, placed: event.target.checked });
          }}
        />
        Placed
      </label>

      <div className="field">
        <span className="field__label">Extra details</span>
        {value.extraDetails.map((detail, index) => (
          <div className="brief-details__pair" key={detail.id}>
            <input
              className="field__input"
              type="text"
              value={detail.key}
              placeholder="Key"
              aria-label={`Extra detail ${String(index + 1)} key`}
              maxLength={60}
              disabled={disabled}
              onChange={(event) => {
                setRow(detail.id, { key: event.target.value });
              }}
            />
            <input
              className="field__input"
              type="text"
              value={detail.value}
              placeholder="Value"
              aria-label={`Extra detail ${String(index + 1)} value`}
              maxLength={500}
              disabled={disabled}
              onChange={(event) => {
                setRow(detail.id, { value: event.target.value });
              }}
            />
            <button
              type="button"
              className="button button--icon button--quiet"
              aria-label={`Remove extra detail ${String(index + 1)}`}
              title="Remove"
              disabled={disabled}
              onClick={() => {
                onChange({
                  ...value,
                  extraDetails: value.extraDetails.filter((other) => other.id !== detail.id),
                });
              }}
            >
              <Icon name="x" />
            </button>
          </div>
        ))}
        <div className="field__actions">
          <button
            type="button"
            className="button button--small"
            disabled={disabled || value.extraDetails.length >= 20}
            onClick={() => {
              onChange({ ...value, extraDetails: [...value.extraDetails, row()] });
            }}
          >
            <Icon name="plus" />
            Add detail
          </button>
        </div>
      </div>
    </div>
  );
}

const empty = <span className="muted">—</span>;

/** The details read: every field listed, an empty one as "—". */
export function BriefDetailsSummary({ details }: { readonly details: BriefDetails }) {
  const text = (value: string | null) => value ?? empty;
  return (
    <>
      <Facts
        items={[
          { label: 'Goal', value: text(details.goal) },
          { label: 'Audience', value: text(details.audience) },
          {
            label: 'Funnel',
            value:
              details.funnelStages.length === 0
                ? empty
                : details.funnelStages.map((stage) => FUNNEL_LABEL[stage]).join(', '),
          },
          { label: 'Angle', value: text(details.angle) },
          { label: 'Placement', value: text(details.placement) },
          { label: 'Placed', value: details.placed ? 'Yes' : 'No' },
        ]}
      />
      {details.extraDetails.length > 0 && (
        <ul className="brief-details__extras">
          {details.extraDetails.map((detail) => (
            <li key={detail.key}>
              <strong>{detail.key}:</strong> {detail.value}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * The "Brief details" panel: read mode with an **Edit** button, which turns it
 * into the form. `onSave` is the owner's: it writes through whichever route
 * fits (a content's, an idea's) and throws on a refusal, whose message is then
 * shown in the form, which stays open.
 */
export function BriefDetailsPanel({
  details,
  onSave,
  id = 'brief-details',
}: {
  readonly details: BriefDetails;
  readonly onSave: (details: BriefDetails) => Promise<void>;
  readonly id?: string;
}) {
  const [draft, setDraft] = useState<BriefDetailsDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = (): void => {
    setDraft(null);
    setError(null);
  };

  const save = (event: FormEvent): void => {
    event.preventDefault();
    if (draft === null) return;
    setSaving(true);
    setError(null);
    onSave(detailsOf(draft))
      .then(() => {
        setDraft(null);
      })
      .catch((cause: unknown) => {
        setError(describeError(cause));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Panel
      id={id}
      title="Brief details"
      icon="tasklist"
      actions={
        draft === null ? (
          <button
            type="button"
            className="button button--small"
            onClick={() => {
              setDraft(draftOf(details));
            }}
          >
            <Icon name="pencil" />
            Edit
          </button>
        ) : undefined
      }
    >
      {draft === null ? (
        <BriefDetailsSummary details={details} />
      ) : (
        <form className="form form--tight" onSubmit={save}>
          <BriefDetailsFields value={draft} onChange={setDraft} disabled={saving} />
          {error !== null && <Notice kind="error">{error}</Notice>}
          <div className="field__actions">
            <button className="button button--primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="button" type="button" disabled={saving} onClick={cancel}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}
